import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../types/graph';
import { ViewableQuestion } from '../types/global';
import { filterOnboardingQuestions } from '../util/questionnaire';
import { useActiveQueue } from './useActiveQueue';

interface UseInfoQuestionCheckerParams {
  currentQuestionIndex: number;
  displayQueue: string[];
}

interface InfoQuestionContext {
  currentQuestionId: string;
  previousQuestionId: string | null;
  nextQuestionId: string | null;
  fullCurrentQuestion: ViewableQuestion | undefined;
  fullPreviousQuestion: ViewableQuestion | null;
  fullNextQuestion: ViewableQuestion | null;
  shouldDisplayPreviousQuestion: boolean;
  isCurrentQuestionInfo: boolean;
  isLastQuestionFromCurrentPage: boolean;
  questionIdForResponse: string;
}

/**
    Hook to manage INFO question navigation logic
 */
export const useInfoQuestionChecker = ({
  currentQuestionIndex,
  displayQueue,
}: UseInfoQuestionCheckerParams): InfoQuestionContext => {
  // Current question
  const currentQuestionId = useMemo(
    () => displayQueue[currentQuestionIndex],
    [displayQueue, currentQuestionIndex],
  );
  const fullCurrentQuestion = useSelector(
    (state: RootState) => state.kb.questions[currentQuestionId],
  );
  const isCurrentQuestionInfo = useMemo(
    () => fullCurrentQuestion?.format === 'INFO',
    [fullCurrentQuestion],
  );

  // Previous question
  const previousQuestionId = useMemo(() => {
    if (currentQuestionIndex > 0) {
      return displayQueue[currentQuestionIndex - 1];
    }
    return null;
  }, [currentQuestionIndex, displayQueue]);
  const fullPreviousQuestion = useSelector((state: RootState) =>
    previousQuestionId ? state.kb.questions[previousQuestionId] : null,
  );
  const shouldDisplayPreviousQuestion = useMemo(
    () => fullPreviousQuestion?.format === 'INFO',
    [fullPreviousQuestion],
  );

  // Next question
  const nextQuestionId = useMemo(() => {
    if (
      fullCurrentQuestion?.format === 'INFO' &&
      currentQuestionIndex < displayQueue.length - 1
    ) {
      return displayQueue[currentQuestionIndex + 1];
    }
    return null;
  }, [fullCurrentQuestion, currentQuestionIndex, displayQueue]);
  const fullNextQuestion = useSelector((state: RootState) =>
    nextQuestionId ? state.kb.questions[nextQuestionId] : null,
  );

  const isLastQuestionFromCurrentPage = useMemo(() => {
    // If we're showing an INFO question with the next question, check if the next question is the last
    if (nextQuestionId) {
      return currentQuestionIndex + 1 === displayQueue.length - 1;
    }
    return currentQuestionIndex === displayQueue.length - 1;
  }, [currentQuestionIndex, displayQueue.length, nextQuestionId]);

  // For INFO questions, use the next question for response validation
  // For questions preceded by INFO, use the current question
  const questionIdForResponse = nextQuestionId || currentQuestionId;

  return {
    currentQuestionId,
    previousQuestionId,
    nextQuestionId,
    fullCurrentQuestion,
    fullPreviousQuestion,
    fullNextQuestion,
    shouldDisplayPreviousQuestion,
    isCurrentQuestionInfo,
    isLastQuestionFromCurrentPage,
    questionIdForResponse,
  };
};

/**
 * Hook to check if we should skip the current question if there is an INFO question before it during backward navigation
 */
export const useInfoQuestionBackwardNavigation = () => {
  const currentQuestionIndex = useSelector(
    (state: RootState) => state.questionnaire.currentQuestionIndex,
  );
  const activeQueue = useActiveQueue();
  const isQueueReady = useSelector(
    (state: RootState) => state.questionnaire.isQueueReady,
  );
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const questions = useSelector((state: RootState) => state.kb.questions);

  const shouldSkipPreviousQuestion = useMemo(() => {
    // Don't skip during page transitions when queue is being rebuilt
    if (!isQueueReady) return false;
    if (currentQuestionIndex <= 0) return false;

    const displayQueue = filterOnboardingQuestions(activeQueue, currentPage);
    const previousQuestionIndex = currentQuestionIndex - 1;

    if (previousQuestionIndex >= 0) {
      const previousQuestionId = displayQueue[previousQuestionIndex];
      const previousQuestion = questions[previousQuestionId];
      return previousQuestion?.format === 'INFO';
    }

    return false;
  }, [isQueueReady, currentQuestionIndex, activeQueue, currentPage, questions]);

  return { shouldSkipPreviousQuestion };
};
